class FaqList extends HTMLElement {
  connectedCallback() {
    this.showAllButton = this.querySelector('[data-action="show-all-faq"]');
    if (!this.showAllButton) {
      return;
    }

    this.hiddenItems = Array.from(this.querySelectorAll(".faq-item--hidden"));
    this.viewMoreLabel = this.showAllButton.getAttribute("data-view-more") || "View all questions";
    this.viewLessLabel = this.showAllButton.getAttribute("data-view-less") || "Show less";
    this.labelElement = this.showAllButton.querySelector(".faq-view-all__label");
    this.accordionBox = this.querySelector(".accordion-box");
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.isAnimating = false;

    this.showAllButton.addEventListener("click", this.onToggleClicked.bind(this));
  }

  async onToggleClicked() {
    if (this.isAnimating || !this.accordionBox) {
      return;
    }

    this.isAnimating = true;
    const shouldExpand = !this.classList.contains("is-expanded");
    const fromHeight = this.accordionBox.clientHeight;

    try {
      this.accordionBox.style.height = `${fromHeight}px`;
      this.accordionBox.style.overflow = "hidden";

      if (shouldExpand) {
        this.setItemsHiddenState(false);

        const expandedHeight = this.accordionBox.scrollHeight;

        await Promise.all([
          this.animateContainerHeight(fromHeight, expandedHeight),
          this.animateItems(true)
        ]);
      } else {
        await this.animateItems(false);

        this.setItemsHiddenState(true);

        const collapsedHeight = this.accordionBox.scrollHeight;
        await this.animateContainerHeight(fromHeight, collapsedHeight);
      }

      this.classList.toggle("is-expanded", shouldExpand);
      this.showAllButton.setAttribute("aria-expanded", String(shouldExpand));

      if (this.labelElement) {
        this.labelElement.textContent = shouldExpand ? this.viewLessLabel : this.viewMoreLabel;
      }
    } finally {
      this.accordionBox.style.height = "auto";
      this.accordionBox.style.overflow = "visible";
      this.isAnimating = false;
    }
  }

  animateItems(shouldExpand) {
    return Promise.all(this.hiddenItems.map((item, index) => this.animateItem(item, shouldExpand, index)));
  }

  setItemsHiddenState(hidden) {
    this.hiddenItems.forEach((item) => {
      item.hidden = hidden;
      item.classList.toggle("hidden", hidden);
    });
  }

  animateContainerHeight(fromHeight, toHeight) {
    if (this.prefersReducedMotion) {
      this.accordionBox.style.height = `${toHeight}px`;
      return Promise.resolve();
    }

    if (fromHeight === toHeight) {
      this.accordionBox.style.height = `${toHeight}px`;
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const element = this.accordionBox;
      let isResolved = false;

      const done = () => {
        if (isResolved) {
          return;
        }
        isResolved = true;
        element.style.transition = "";
        resolve();
      };

      const onTransitionEnd = (event) => {
        if (event.target === element && event.propertyName === "height") {
          element.removeEventListener("transitionend", onTransitionEnd);
          done();
        }
      };

      element.addEventListener("transitionend", onTransitionEnd);

      requestAnimationFrame(() => {
        element.style.transition = "height 250ms ease";
        element.style.height = `${toHeight}px`;
      });

      setTimeout(() => {
        element.removeEventListener("transitionend", onTransitionEnd);
        done();
      }, 320);
    });
  }

  animateItem(item, shouldExpand, index) {
    if (this.prefersReducedMotion) {
      return Promise.resolve();
    }

    const delay = index * 10;

    const keyframes = shouldExpand
      ? [
          { opacity: 0, transform: "translateY(-4px)" },
          { opacity: 1, transform: "translateY(0)" }
        ]
      : [
          { opacity: 1, transform: "translateY(0)" },
          { opacity: 0, transform: "translateY(-4px)" }
        ];

    const options = shouldExpand
      ? { duration: 150, delay, easing: "ease-out", fill: "both" }
      : { duration: 150, delay, easing: "ease-in", fill: "both" };

    return item
      .animate(keyframes, options)
      .finished.catch(() => {});
  }
}

if (!window.customElements.get("faq-list")) {
  window.customElements.define("faq-list", FaqList);
}
